import { debounce } from 'throttle-debounce';

import { $config, $waitForClient } from '@client/core/atoms/config.js';
import { addLifecycleEvent } from '@client/core/atoms/metrics.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import * as RelevantDigital from '@client/core/relevantDigital/index.js';
import { debouncedSendMetrics } from '@client/core/services/metrics.js';
import {
  DisplayPlacementId,
  hasItems,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';

import { debug } from './.debug.js';
import { Api } from './Api/index.js';
import { isLowPerformingSubverticalSection } from './utils/shouldLoadRelevantDigitalSlots.js';

export namespace BatchedSlots {
  let queue = new Array<googletag.Slot>();
  let normalQueue = new Array<googletag.Slot>();
  let immediateQueue = new Array<googletag.Slot>();

  export const IMMEDIATE_SLOTS = [
    DisplayPlacementId.AdvtTop1,
    DisplayPlacementId.AdvtRight1,
    DisplayPlacementId.AdvtLeft1,
    DisplayPlacementId.AdvtWallpaper,
    DisplayPlacementId.AdvtAudience
  ];

  export namespace SplitBatchQueue {
    export function add(slot: googletag.Slot) {
      const slotId = slot.getSlotElementId();
      const isImmediatePlacement = IMMEDIATE_SLOTS.includes(
        slotId as DisplayPlacementId
      );

      if (isImmediatePlacement) {
        immediateQueue.push(slot);
        SplitBatchImmediateQueue.flush.debounced();
      } else {
        normalQueue.push(slot);
        SplitBatchQueue.flush.debounced();
      }

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) {
            if (isImmediatePlacement && immediateQueue.length > 0) {
              SplitBatchImmediateQueue.flush.debounced();
            } else if (!isImmediatePlacement && normalQueue.length > 0) {
              SplitBatchQueue.flush.debounced();
            }
          }
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...normalQueue];
      normalQueue = [];

      load(slotsToLoad).catch((error: unknown) => {
        if (hasItems(slotsToLoad)) {
          debug.message`Failed batch request for ${slotsToLoad.length} slot(s)`(
            ...slotsToLoad,
            error
          ).error();
        } else {
          debug
            .message('Failed batch request with no slots provided', error)
            .error();
        }
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, SplitBatchQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace SplitBatchImmediateQueue {
    export function flush() {
      const slotsToLoad = [...immediateQueue];
      immediateQueue = [];

      load(slotsToLoad).catch((error: unknown) => {
        if (hasItems(slotsToLoad)) {
          debug.message`Failed immediate batch request for ${slotsToLoad.length} slot(s)`(
            ...slotsToLoad,
            error
          ).error();
        } else {
          debug
            .message(
              'Failed immediate batch request with no slots provided',
              error
            )
            .error();
        }
      });
    }

    export namespace flush {
      /** Debounce function that accumulates immediate slots and refreshes them after 50ms. */
      export const debounced = debounce(50, SplitBatchImmediateQueue.flush, {
        atBegin: false
      });
    }
  }

  export namespace Queue {
    export function add(slot: googletag.Slot) {
      queue.push(slot);
      flush.debounced();

      const waitForClient = $waitForClient.get();
      if (waitForClient) {
        $waitForClient.subscribe((wait) => {
          if (!wait) flush.debounced();
        });
      }
    }

    export function flush() {
      const slotsToLoad = [...queue];
      queue = [];

      load(slotsToLoad).catch((error: unknown) => {
        if (hasItems(slotsToLoad)) {
          debug.message`Failed batch request for ${slotsToLoad.length} slot(s)`(
            ...slotsToLoad,
            error
          ).error();
        } else {
          debug
            .message('Failed batch request with no slots provided', error)
            .error();
        }
      });
    }

    export namespace flush {
      /** Debounce function that accumulates slots and refreshes them after 200ms. */
      export const debounced = debounce(200, Queue.flush, { atBegin: false });
    }
  }

  export async function load(
    slots: Array<googletag.Slot>,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ) {
    const { brand, subvertical, pageType } = $config.get() ?? {};

    if (hasItems(slots)) {
      debug.message`Requesting batch of ${slots.length} slot(s)`(...slots).info(
        (log) => log()
      );
    } else {
      debug.message('Requesting batch with no slots provided').info();
    }

    const formattedSlotIds = await Api(
      (googletag) => {
        for (const slot of slots) {
          googletag.display(slot);
        }

        return slots.map((slot) => slot.getSlotElementId()).join(', ');
      },
      ...abortSignals
    );

    if (
      !isFeatureEnabled(UNLEASH_FEATURE_NAME.killswitchPrebid) &&
      !isLowPerformingSubverticalSection(brand, pageType, subvertical)
    ) {
      try {
        await RelevantDigital.Slots.load(slots);

        if (hasItems(slots)) {
          debug.message`Prebid auction completed for ${slots.length} slot(s)`(
            ...slots
          ).success((log) => log());
        } else {
          debug
            .message('Prebid auction completed with no slots provided')
            .success();
        }

        addLifecycleEvent(`Prebid auction completed for ${formattedSlotIds}`);
      } catch (error) {
        if (hasItems(slots)) {
          debug.message`Prebid auction failed for ${slots.length} slot(s)`(
            ...slots,
            error
          ).error();
        } else {
          debug
            .message('Prebid auction failed with no slots provided', error)
            .error();
        }

        addLifecycleEvent(`Prebid auction failed for ${formattedSlotIds}`);
      }
    }

    await Api(...abortSignals)
      .pubads()
      .refresh(slots);

    if (hasItems(slots)) {
      debug.message`Completed batch request for ${slots.length} slot(s)`(
        ...slots
      ).success((log) => log());
    } else {
      debug.message('Completed batch request with no slots provided').success();
    }

    addLifecycleEvent(`GAM Load batch of Ads for ${formattedSlotIds}`);
    debouncedSendMetrics();
  }
}
